import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { DEV_BYPASS_ACCESS_TOKEN, isDevAuthBypassEnabled } from '@/app/devMode';
import { STORAGE_KEY_TOKEN } from '@/constants/constants';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useSettingsSync } from '@/hooks/useSettingsSync';
import { showToast } from '@/shared/ui/Toast/showToast';

/**
 * Handles core application initialization logic, including Auth0 token sync,
 * online status tracking, and settings synchronization.
 */
export function useAppInitialization() {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  useSettingsSync();
  const { isOnline } = useOnlineStatus();
  const [isTokenReady, setIsTokenReady] = useState(false);

  const prevIsOnline = useRef(isOnline);

  // WHY: the persistent OfflineBanner (App.tsx) is the offline live region now, so going offline is NOT
  // toasted here — that would double-announce the same state (see OfflineBanner + pattern-error-handling
  // §6). Only the transient "back online" recovery stays a toast; the banner's disappearance is silent.
  useEffect(() => {
    if (!prevIsOnline.current && isOnline) {
      showToast.success('You are back online.');
    }
    prevIsOnline.current = isOnline;
  }, [isOnline]);

  // Fetch and store Auth0 access token when authenticated
  useEffect(() => {
    const storeAccessToken = async () => {
      if (isDevAuthBypassEnabled) {
        localStorage.setItem(STORAGE_KEY_TOKEN, DEV_BYPASS_ACCESS_TOKEN);
        setIsTokenReady(true);
        return;
      }

      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          localStorage.setItem(STORAGE_KEY_TOKEN, token);
        } catch (error) {
          console.error('Error getting access token:', error);
        }
        setIsTokenReady(true);
      } else if (!isLoading) {
        setIsTokenReady(true);
      }
    };
    storeAccessToken();
  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  return {
    isReady: !(
      (isLoading && !isDevAuthBypassEnabled) ||
      ((isAuthenticated || isDevAuthBypassEnabled) && !isTokenReady)
    ),
    isAuthenticated: isAuthenticated || isDevAuthBypassEnabled
  };
}
